// Fees → User overrides. Special rates for specific people: a user
// override beats every policy for that user's newly placed orders.
//
// Rows lead with the person (avatar, name, email), never a ULID; the
// override's market scope shows the market's name. Creating an
// override happens right here through a user autocomplete, so the
// old "go find the user detail page first" detour is gone.
//
// Currency: a POLICY names the currency it prices since SM-510, but a
// user override does not — SM-510 keyed `fee_profiles` by currency and
// left `user_fee_overrides` alone, so one override still prices every
// book the markets it covers run.
//
// That is not an oversight to paper over here. order-service consults a
// user override on the CASH book only (a Game leg takes the configured
// same-currency policy rate), so the rate on this page is a Gold rate in
// practice — and the row itself stores nothing that says so. `?currency=`
// therefore narrows by what an override's markets CAN be charged in,
// applied to the page the server returned, exactly as it did before.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  fetchAllUserFeeOverrides,
  fetchUsersByIDs,
  removeUserFeeOverride,
  setUserFeeOverride,
  userFeeKeys,
  type UserFeeOverride,
} from "@/lib/queries";
import type { MarketItem } from "@/lib/types";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { FeeRateInput } from "@/components/fees/FeeRateInput";
import { MarketPickField } from "@/components/fees/MarketPickField";
import {
  UserPickField,
  userLabel,
  type PickedUser,
} from "@/components/fees/UserPickField";
import { RatePair } from "@/components/fees/PolicyList";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { useMarketNames } from "@/hooks/useCatalogNames";
import { fmtRelative } from "@/lib/format";
import { FeeCurrencyFilter } from "@/components/fees/FeeCurrencyFilter";
import { FeeCurrencyMarks } from "@/components/fees/FeeCurrencyLabel";
import { useFeeReach, type FeeReachIndex } from "@/components/fees/useFeeReach";
import {
  exampleCurrency,
  feeCurrencySearch,
  parseFeeCurrencySearch,
  reachAllowsCurrency,
  scopedCurrency,
  USER_OVERRIDE_CURRENCY_NOTE,
  type FeeCurrencyParam,
  type FeeReach,
} from "@/components/fees/feeCurrency";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";

export const Route = createFileRoute("/_app/treasury/fees/users")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { currency?: FeeCurrencyParam } => {
    const currency = parseFeeCurrencySearch(search.currency);
    return currency ? { currency } : {};
  },
  component: UserOverridesPage,
});

const PAGE = 50;
const DEFAULT_PRECISION = 6;

function UserOverridesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currencyParam = Route.useSearch().currency;
  const currency = scopedCurrency(currencyParam);
  const currencyName = useCurrencyName(currency ?? null);
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<UserFeeOverride | null>(null);

  const q = useQuery({
    queryKey: userFeeKeys.page(PAGE, page * PAGE),
    queryFn: () => fetchAllUserFeeOverrides({ limit: PAGE, offset: page * PAGE }),
    staleTime: 30_000,
  });
  const items = q.data?.items ?? [];

  // Clamp when the total shrinks under the current offset (e.g. the
  // last row on the last page was removed) — otherwise the operator
  // strands on an out-of-range page that reads as "no overrides".
  const total = q.data?.page.total;
  useEffect(() => {
    if (total === undefined || page === 0) return;
    const maxPage = Math.max(0, Math.ceil(total / PAGE) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [total, page]);

  // Resolve ids → people and market names.
  const userIds = useMemo(
    () => [...new Set(items.map((o) => o.user_id))],
    [items],
  );
  const usersQ = useQuery({
    queryKey: ["admin-user-fees", "user-labels", userIds.join(",")] as const,
    queryFn: () => fetchUsersByIDs(userIds),
    enabled: userIds.length > 0,
    staleTime: 60_000,
  });
  const userMap = useMemo(
    () => new Map((usersQ.data ?? []).map((u) => [u.user_id, u])),
    [usersQ.data],
  );
  const marketNames = useMarketNames(
    useMemo(
      () => items.map((o) => o.market_id).filter((x): x is string => !!x),
      [items],
    ),
  );

  // What each override's markets are charged in (SM-503). A row scoped
  // to one market takes that market's books; a user-wide row reaches the
  // whole catalog and takes its union.
  const reachIndex = useFeeReach();
  const reachOf = (o: UserFeeOverride): FeeReach =>
    o.market_id ? reachIndex.forMarket(o.market_id) : reachIndex.forCatalog();
  // The currency scope filters the PAGE the server returned, because the
  // overrides endpoint has no currency of its own to filter on. The
  // page total below therefore keeps counting every override, and the
  // note under the list says how many this page is not showing — a
  // filtered count presented as the total is the failure to avoid.
  const visibleItems = items.filter((o) =>
    reachAllowsCurrency(reachOf(o), currency),
  );
  const hiddenByCurrency = items.length - visibleItems.length;

  const removeMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      removeUserFeeOverride(id, reason || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userFeeKeys.all });
      setRemoveTarget(null);
    },
    meta: { successMessage: "Override removed. The user pays standard rates again." },
  });

  const removeName = removeTarget
    ? labelForUser(removeTarget.user_id, userMap.get(removeTarget.user_id))
    : "";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">
            User fee overrides
          </h1>
          <p className="max-w-[75ch] text-sm text-muted">
            Special rates for specific people. An override here beats every
            policy for that user&apos;s newly placed orders; resting orders
            keep the rate they locked at placement.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <FeeCurrencyFilter
            includeLegacy={false}
            className="w-48"
            value={currencyParam}
            onChange={(next) =>
              navigate({
                to: "/treasury/fees/users",
                search: feeCurrencySearch(next),
                replace: true,
              })
            }
          />
          {!creating && (
            <Button size="sm" onClick={() => setCreating(true)}>
              New override
            </Button>
          )}
        </div>
      </div>

      <p className="max-w-[75ch] rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
        {USER_OVERRIDE_CURRENCY_NOTE}
      </p>

      {creating && (
        <CreateOverrideCard
          onDone={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: userFeeKeys.all });
          }}
          onCancel={() => setCreating(false)}
          reachIndex={reachIndex}
        />
      )}

      {q.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : q.isError ? (
        <LoadFailed
          what="user overrides"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : items.length > 0 && visibleItems.length === 0 ? (
        <EmptyState
          title={`No overrides in ${currencyName}`}
          description={`None of the overrides on this page covers a market that can be charged in ${currencyName}. Switch to another currency, or to every currency, to see them.`}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No user overrides"
          description="Everyone pays the rate resolved from the fee policies."
          action={
            !creating ? (
              <Button size="sm" onClick={() => setCreating(true)}>
                New override
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="overflow-hidden rounded-md border border-border bg-surface">
          {visibleItems.map((o) => {
            const u = userMap.get(o.user_id);
            const name = labelForUser(o.user_id, u);
            return (
              <li
                key={o.override_id}
                className="flex items-center gap-3 border-b border-border/60 px-4 py-2.5 last:border-0"
              >
                <Avatar url={u?.avatar_url} name={name} size={28} />
                <span className="min-w-0 flex-1">
                  <Link
                    to="/users/$id"
                    params={{ id: o.user_id }}
                    className="block truncate text-sm font-medium text-fg hover:text-accent hover:underline"
                  >
                    {name}
                  </Link>
                  <span className="mt-0.5 block truncate text-2xs text-muted">
                    {u?.email && u.display_name ? `${u.email} · ` : ""}
                    {o.market_id
                      ? marketNames.get(o.market_id) ?? "one market"
                      : "All markets"}
                  </span>
                </span>
                <span className="hidden w-24 justify-end md:flex">
                  <FeeCurrencyMarks codes={reachOf(o).currencies} compact />
                </span>
                <RatePair
                  taker={o.taker_fee_rate}
                  maker={o.maker_fee_rate}
                  precision={o.fee_rate_precision}
                />
                <span className="hidden w-20 truncate text-right text-2xs text-t-4 lg:block">
                  {fmtRelative(o.created_at)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRemoveTarget(o)}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {hiddenByCurrency > 0 && (
        <p className="text-2xs text-t-3">
          {hiddenByCurrency === 1
            ? `1 more override on this page covers markets never charged in ${currencyName}.`
            : `${hiddenByCurrency} more overrides on this page cover markets never charged in ${currencyName}.`}
        </p>
      )}

      {q.isSuccess && (q.data?.page.total ?? 0) > PAGE && (
        <Pagination
          page={{
            ...q.data!.page,
            has_more:
              q.data!.page.offset + q.data!.page.limit < q.data!.page.total,
          }}
          currentPage={page}
          onPageChange={setPage}
          itemLabel="overrides"
        />
      )}

      <ConfirmModal
        open={removeTarget !== null}
        tone="danger"
        title={`Remove the override for ${removeName}?`}
        description={
          <p>
            Their next orders go back to the standard rate for each market.
            Resting orders keep the rate they locked at placement.
          </p>
        }
        confirmLabel="Remove"
        cancelLabel="Keep it"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={removeMut.isPending}
        error={removeMut.isError ? (removeMut.error as Error).message : null}
        onConfirm={(reason) =>
          removeTarget &&
          removeMut.mutate({ id: removeTarget.override_id, reason })
        }
        onCancel={() => {
          removeMut.reset();
          setRemoveTarget(null);
        }}
      />
    </div>
  );
}

function labelForUser(
  id: string,
  u?: { display_name?: string; email?: string },
): string {
  return u?.display_name || u?.email || id;
}

function CreateOverrideCard({
  onDone,
  onCancel,
  reachIndex,
}: {
  onDone: () => void;
  onCancel: () => void;
  /** Shared with the list so the form and the rows read one catalog. */
  reachIndex: FeeReachIndex;
}) {
  const [user, setUser] = useState<PickedUser | null>(null);
  const [scope, setScope] = useState<"all" | "market">("all");
  const [market, setMarket] = useState<MarketItem | null>(null);
  const [maker, setMaker] = useState("0");
  const [taker, setTaker] = useState("0");
  const [note, setNote] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      setUserFeeOverride({
        user_id: user!.user_id,
        market_id: scope === "market" ? market?.market_id : undefined,
        maker_fee_rate: maker || "0",
        taker_fee_rate: taker || "0",
        fee_rate_precision: DEFAULT_PRECISION,
        note: note.trim() || undefined,
      }),
    onSuccess: onDone,
    meta: {
      successMessage: "Override saved. It applies to the user's next orders.",
    },
  });

  // What the override will be collected in: one market's books, or the
  // whole catalog's when it covers every market (SM-503).
  const reach =
    scope === "market"
      ? market
        ? reachIndex.forMarketRow(market)
        : null
      : reachIndex.forCatalog();
  const exampleCurrencies = reach?.currencies.length
    ? reach.currencies
    : [exampleCurrency(undefined)];

  const canSubmit =
    user !== null && (scope === "all" || market !== null) && !mut.isPending;

  return (
    <Card>
      <CardBody className="space-y-5">
        <UserPickField value={user} onChange={setUser} />

        <SegmentedControl<"all" | "market">
          label="Applies to"
          full
          options={[
            { value: "all", label: "All markets" },
            { value: "market", label: "One market" },
          ]}
          value={scope}
          onChange={setScope}
        />
        {scope === "market" && (
          <MarketPickField value={market} onChange={setMarket} />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <FeeRateInput
            label="Taker fee"
            value={taker}
            precision={DEFAULT_PRECISION}
            onChange={setTaker}
            currencies={exampleCurrencies}
          />
          <FeeRateInput
            label="Maker fee"
            value={maker}
            precision={DEFAULT_PRECISION}
            onChange={setMaker}
            showPreview={false}
            hint="Charged to the resting side of a fill. Usually zero."
          />
        </div>

        <div>
          <label
            htmlFor="user-override-note"
            className="text-2xs font-semibold uppercase tracking-wide text-t-3"
          >
            Note (audit log)
          </label>
          <input
            id="user-override-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. VIP tier 1"
            className="mt-1.5 h-10 w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm text-t-1 placeholder:text-t-4 focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15"
          />
        </div>

        {mut.isError && (
          <p className="text-2xs text-danger">{(mut.error as Error).message}</p>
        )}

        {/* The worked examples live under the rate field itself; this
            line names the currencies once, because "all markets" reaches
            books the picker never mentions (SM-503). */}
        {reach?.known && reach.currencies.length > 0 && (
          <p className="flex flex-wrap items-center gap-1.5 text-2xs leading-relaxed text-t-3">
            This rate is charged in:
            <FeeCurrencyMarks codes={reach.currencies} />
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => mut.mutate()}
            disabled={!canSubmit}
            loading={mut.isPending}
          >
            {user
              ? `Save override for ${userLabel(user)}`
              : "Save override"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
